/* eslint-disable */
import { Timestamp } from "../../../../google/protobuf/timestamp";
import { CreditRange } from "../../../mortgage/data/credit_range";
import {
  MarketingChannel,
  marketingChannelFromJSON,
  marketingChannelToJSON,
  Partnership,
  partnershipFromJSON,
  partnershipToJSON,
  Timeline,
  timelineFromJSON,
  timelineToJSON,
} from "../../common/common";

export const protobufPackage = "od_protos.sell.seller.data";

/**
 * SellerDetails contains information provided to us from the seller or
 * set by the source service (e.g. Reception or Partnership). Data specified
 * here may be updated if it is updated in the source service.
 *
 * Next ID: 21
 */
export interface SellerDetails {
  /** True if the customer is an agent representing the home seller. */
  agent: boolean;
  /** Whether the home is currently listed or not. */
  listed: boolean;
  /** Whether the home is a potential trade in or not. */
  tradeIn: boolean;
  /** Whether the home is a potential trade in bundle (sell + buy + finance). */
  tradeInBundle: boolean;
  /** Whether or not the user is in the new trade-in dashboard experience. */
  isTradeInDashboard: boolean;
  /** Whether or not the customer has visited the offer dashboard. */
  offerDashboardVisited: boolean;
  /** Whether or not the customer answered that they are actively looking to buy */
  activelyLooking: boolean;
  /** The channel the seller came through */
  channel: MarketingChannel;
  /** The home builder partnership the seller came in through, if any. */
  partnership: Partnership;
  /** Seller's sale timeline. */
  timeline: Timeline;
  /** Seller's own estimate of the value of their home. */
  priceEstimateCents: number;
  /** Photos submitted by the seller as a list of cabinet ids. */
  photoIds: string[];
  /**
   * Customer UUID for the representative of the partner
   * organization that referred the lead to Opendoor.
   */
  referrerContactId: string;
  /** Specifies the time the offer should be delivered to the customer. */
  offerDeliveryTime?:
    | Date
    | undefined;
  /**
   * Following 4 fields are inputs to calculating buying power using the mortgage service,
   * so types are kept consistent with those in prequalification answers.
   * Seller's annual income in cents.
   */
  annualIncomeCents: number;
  /** Seller's down payment in cents for their next home. */
  downPaymentCents: number;
  /** Seller's monthly debt/expenses in cents. */
  monthlyDebtsCents: number;
  /** Seller's credit score range. */
  creditScoreRange?:
    | CreditRange
    | undefined;
  /** Seller's goal price. */
  goalPriceCents: number;
  /** Whether or not the customer has visited the new dynamic moving hub */
  hasVisitedHub: boolean;
  /** Determines if customer is eligible for an assumable loan from Roam */
  eligibleForRoamAssumableLoan: boolean;
}

/** Next ID: 4 */
export interface HistoricalSellerDetails {
  sellerDetails?: SellerDetails | undefined;
  updatedByToken: string;
  updatedAt?: Date | undefined;
}

function createBaseSellerDetails(): SellerDetails {
  return {
    agent: false,
    listed: false,
    tradeIn: false,
    tradeInBundle: false,
    isTradeInDashboard: false,
    offerDashboardVisited: false,
    activelyLooking: false,
    channel: 0,
    partnership: 0,
    timeline: 0,
    priceEstimateCents: 0,
    photoIds: [],
    referrerContactId: "",
    offerDeliveryTime: undefined,
    annualIncomeCents: 0,
    downPaymentCents: 0,
    monthlyDebtsCents: 0,
    creditScoreRange: undefined,
    goalPriceCents: 0,
    hasVisitedHub: false,
    eligibleForRoamAssumableLoan: false,
  };
}

export const SellerDetails = {
  fromJSON(object: any): SellerDetails {
    return {
      agent: isSet(object.agent) ? Boolean(object.agent) : false,
      listed: isSet(object.listed) ? Boolean(object.listed) : false,
      tradeIn: isSet(object.trade_in) ? Boolean(object.trade_in) : false,
      tradeInBundle: isSet(object.trade_in_bundle) ? Boolean(object.trade_in_bundle) : false,
      isTradeInDashboard: isSet(object.is_trade_in_dashboard) ? Boolean(object.is_trade_in_dashboard) : false,
      offerDashboardVisited: isSet(object.offer_dashboard_visited) ? Boolean(object.offer_dashboard_visited) : false,
      activelyLooking: isSet(object.actively_looking) ? Boolean(object.actively_looking) : false,
      channel: isSet(object.channel) ? marketingChannelFromJSON(object.channel) : 0,
      partnership: isSet(object.partnership) ? partnershipFromJSON(object.partnership) : 0,
      timeline: isSet(object.timeline) ? timelineFromJSON(object.timeline) : 0,
      priceEstimateCents: isSet(object.price_estimate_cents) ? Number(object.price_estimate_cents) : 0,
      photoIds: Array.isArray(object?.photo_ids) ? object.photo_ids.map((e: any) => String(e)) : [],
      referrerContactId: isSet(object.referrer_contact_id) ? String(object.referrer_contact_id) : "",
      offerDeliveryTime: isSet(object.offer_delivery_time) ? fromJsonTimestamp(object.offer_delivery_time) : undefined,
      annualIncomeCents: isSet(object.annual_income_cents) ? Number(object.annual_income_cents) : 0,
      downPaymentCents: isSet(object.down_payment_cents) ? Number(object.down_payment_cents) : 0,
      monthlyDebtsCents: isSet(object.monthly_debts_cents) ? Number(object.monthly_debts_cents) : 0,
      creditScoreRange: isSet(object.credit_score_range) ? CreditRange.fromJSON(object.credit_score_range) : undefined,
      goalPriceCents: isSet(object.goal_price_cents) ? Number(object.goal_price_cents) : 0,
      hasVisitedHub: isSet(object.has_visited_hub) ? Boolean(object.has_visited_hub) : false,
      eligibleForRoamAssumableLoan: isSet(object.eligible_for_roam_assumable_loan)
        ? Boolean(object.eligible_for_roam_assumable_loan)
        : false,
    };
  },

  toJSON(message: SellerDetails): unknown {
    const obj: any = {};
    if (message.agent === true) {
      obj.agent = message.agent;
    }
    if (message.listed === true) {
      obj.listed = message.listed;
    }
    if (message.tradeIn === true) {
      obj.trade_in = message.tradeIn;
    }
    if (message.tradeInBundle === true) {
      obj.trade_in_bundle = message.tradeInBundle;
    }
    if (message.isTradeInDashboard === true) {
      obj.is_trade_in_dashboard = message.isTradeInDashboard;
    }
    if (message.offerDashboardVisited === true) {
      obj.offer_dashboard_visited = message.offerDashboardVisited;
    }
    if (message.activelyLooking === true) {
      obj.actively_looking = message.activelyLooking;
    }
    if (message.channel !== 0) {
      obj.channel = marketingChannelToJSON(message.channel);
    }
    if (message.partnership !== 0) {
      obj.partnership = partnershipToJSON(message.partnership);
    }
    if (message.timeline !== 0) {
      obj.timeline = timelineToJSON(message.timeline);
    }
    if (message.priceEstimateCents !== 0) {
      obj.price_estimate_cents = Math.round(message.priceEstimateCents);
    }
    if (message.photoIds?.length) {
      obj.photo_ids = message.photoIds;
    }
    if (message.referrerContactId !== "") {
      obj.referrer_contact_id = message.referrerContactId;
    }
    if (message.offerDeliveryTime !== undefined) {
      obj.offer_delivery_time = message.offerDeliveryTime.toISOString();
    }
    if (message.annualIncomeCents !== 0) {
      obj.annual_income_cents = Math.round(message.annualIncomeCents);
    }
    if (message.downPaymentCents !== 0) {
      obj.down_payment_cents = Math.round(message.downPaymentCents);
    }
    if (message.monthlyDebtsCents !== 0) {
      obj.monthly_debts_cents = Math.round(message.monthlyDebtsCents);
    }
    if (message.creditScoreRange !== undefined) {
      obj.credit_score_range = CreditRange.toJSON(message.creditScoreRange);
    }
    if (message.goalPriceCents !== 0) {
      obj.goal_price_cents = Math.round(message.goalPriceCents);
    }
    if (message.hasVisitedHub === true) {
      obj.has_visited_hub = message.hasVisitedHub;
    }
    if (message.eligibleForRoamAssumableLoan === true) {
      obj.eligible_for_roam_assumable_loan = message.eligibleForRoamAssumableLoan;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SellerDetails>, I>>(base?: I): SellerDetails {
    return SellerDetails.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SellerDetails>, I>>(object: I): SellerDetails {
    const message = createBaseSellerDetails();
    message.agent = object.agent ?? false;
    message.listed = object.listed ?? false;
    message.tradeIn = object.tradeIn ?? false;
    message.tradeInBundle = object.tradeInBundle ?? false;
    message.isTradeInDashboard = object.isTradeInDashboard ?? false;
    message.offerDashboardVisited = object.offerDashboardVisited ?? false;
    message.activelyLooking = object.activelyLooking ?? false;
    message.channel = object.channel ?? 0;
    message.partnership = object.partnership ?? 0;
    message.timeline = object.timeline ?? 0;
    message.priceEstimateCents = object.priceEstimateCents ?? 0;
    message.photoIds = object.photoIds?.map((e) => e) || [];
    message.referrerContactId = object.referrerContactId ?? "";
    message.offerDeliveryTime = object.offerDeliveryTime ?? undefined;
    message.annualIncomeCents = object.annualIncomeCents ?? 0;
    message.downPaymentCents = object.downPaymentCents ?? 0;
    message.monthlyDebtsCents = object.monthlyDebtsCents ?? 0;
    message.creditScoreRange = (object.creditScoreRange !== undefined && object.creditScoreRange !== null)
      ? CreditRange.fromPartial(object.creditScoreRange)
      : undefined;
    message.goalPriceCents = object.goalPriceCents ?? 0;
    message.hasVisitedHub = object.hasVisitedHub ?? false;
    message.eligibleForRoamAssumableLoan = object.eligibleForRoamAssumableLoan ?? false;
    return message;
  },
};

function createBaseHistoricalSellerDetails(): HistoricalSellerDetails {
  return { sellerDetails: undefined, updatedByToken: "", updatedAt: undefined };
}

export const HistoricalSellerDetails = {
  fromJSON(object: any): HistoricalSellerDetails {
    return {
      sellerDetails: isSet(object.sellerDetails) ? SellerDetails.fromJSON(object.sellerDetails) : undefined,
      updatedByToken: isSet(object.updatedByToken) ? String(object.updatedByToken) : "",
      updatedAt: isSet(object.updatedAt) ? fromJsonTimestamp(object.updatedAt) : undefined,
    };
  },

  toJSON(message: HistoricalSellerDetails): unknown {
    const obj: any = {};
    if (message.sellerDetails !== undefined) {
      obj.sellerDetails = SellerDetails.toJSON(message.sellerDetails);
    }
    if (message.updatedByToken !== "") {
      obj.updatedByToken = message.updatedByToken;
    }
    if (message.updatedAt !== undefined) {
      obj.updatedAt = message.updatedAt.toISOString();
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HistoricalSellerDetails>, I>>(base?: I): HistoricalSellerDetails {
    return HistoricalSellerDetails.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HistoricalSellerDetails>, I>>(object: I): HistoricalSellerDetails {
    const message = createBaseHistoricalSellerDetails();
    message.sellerDetails = (object.sellerDetails !== undefined && object.sellerDetails !== null)
      ? SellerDetails.fromPartial(object.sellerDetails)
      : undefined;
    message.updatedByToken = object.updatedByToken ?? "";
    message.updatedAt = object.updatedAt ?? undefined;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends Array<infer U> ? Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends { $case: string } ? { [K in keyof Omit<T, "$case">]?: DeepPartial<T[K]> } & { $case: T["$case"] }
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function fromTimestamp(t: Timestamp): Date {
  let millis = (t.seconds || 0) * 1_000;
  millis += (t.nanos || 0) / 1_000_000;
  return new Date(millis);
}

function fromJsonTimestamp(o: any): Date {
  if (o instanceof Date) {
    return o;
  } else if (typeof o === "string") {
    return new Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
